import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Network, Search, ArrowDownToLine, ArrowUpFromLine, Users, 
  Share2, AlertTriangle, Settings, RefreshCw, Loader2,
  ChevronDown, ChevronUp, ExternalLink, Filter, TrendingUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PERIODOS = [
  { value: 'DIARIO', label: 'Último día' },
  { value: 'SEMANAL', label: 'Últimos 7 días' },
  { value: 'MENSUAL', label: 'Últimos 30 días' },
  { value: 'TRIMESTRAL', label: 'Últimos 90 días' }
];

export const VinculosAnalysis = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('patrones');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Data states
  const [analisisCompleto, setAnalisisCompleto] = useState(null);
  const [cuentaVinculos, setCuentaVinculos] = useState(null);
  const [comparacionResult, setComparacionResult] = useState(null);
  const [config, setConfig] = useState(null);
  
  // Filter states
  const [periodo, setPeriodo] = useState('MENSUAL');
  const [montoMinimo, setMontoMinimo] = useState(0);
  const [cuentaBusqueda, setCuentaBusqueda] = useState('');
  const [cuenta1Comparar, setCuenta1Comparar] = useState('');
  const [cuenta2Comparar, setCuenta2Comparar] = useState('');
  const [cuitBusqueda, setCuitBusqueda] = useState('');
  
  // Results states
  const [cuitAnalisis, setCuitAnalisis] = useState(null);
  const [analisisMasivo, setAnalisisMasivo] = useState(null);
  
  // UI states
  const [expandedRows, setExpandedRows] = useState({});
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [tipoPatronFiltro, setTipoPatronFiltro] = useState('todos');
  const [generandoAlertas, setGenerandoAlertas] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/vinculos/config');
      setConfig(res.data);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const ejecutarAnalisis = async () => {
    setLoading(true);
    try {
      const res = await api.get('/vinculos/analisis/completo', {
        params: { periodo, monto_minimo: montoMinimo }
      });
      setAnalisisCompleto(res.data);
      toast.success(`Análisis completado: ${res.data.resumen.total_patrones} patrones detectados`);
    } catch (error) {
      toast.error('Error al ejecutar análisis');
    } finally {
      setLoading(false);
    }
  };

  const buscarCuenta = async () => {
    if (!cuentaBusqueda) {
      toast.error('Ingrese un CBU/CVU');
      return;
    }
    
    setLoading(true);
    setCuentaVinculos(null); // Reset previous results
    try {
      const res = await api.get(`/vinculos/cuenta/${cuentaBusqueda}`, {
        params: { periodo }
      });
      // Set data and switch tab in sequence
      setCuentaVinculos(res.data);
      toast.success(`Cuenta encontrada: ${res.data.total_destinos} destinos, ${res.data.total_origenes} orígenes`);
    } catch (error) {
      toast.error('Error al buscar cuenta');
      setCuentaVinculos(null);
    } finally {
      setLoading(false);
    }
  };

  const compararCuentas = async () => {
    if (!cuenta1Comparar || !cuenta2Comparar) {
      toast.error('Ingrese ambas cuentas');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.get('/vinculos/comparar', {
        params: { cuenta1: cuenta1Comparar, cuenta2: cuenta2Comparar, periodo }
      });
      setComparacionResult(res.data);
      setActiveTab('comparar');
    } catch (error) {
      toast.error('Error al comparar cuentas');
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async (field, value) => {
    setSaving(true);
    try {
      await api.patch('/vinculos/config', { [field]: value });
      setConfig({ ...config, [field]: value });
      toast.success('Configuración actualizada');
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const buscarPorCuit = async () => {
    if (!cuitBusqueda) {
      toast.error('Ingrese un CUIT');
      return;
    }
    
    setLoading(true);
    setCuitAnalisis(null);
    try {
      const res = await api.get(`/vinculos/analisis/por-cuit/${cuitBusqueda}`, {
        params: { periodo }
      });
      setCuitAnalisis(res.data);
      toast.success(`CUIT ${cuitBusqueda}: ${res.data.total_cuentas} cuentas encontradas`);
    } catch (error) {
      toast.error('Error al buscar CUIT');
      setCuitAnalisis(null);
    } finally {
      setLoading(false);
    }
  };

  const ejecutarAnalisisMasivo = async () => {
    setLoading(true);
    setAnalisisMasivo(null);
    try {
      const res = await api.get('/vinculos/analisis/masivo-waya', {
        params: { periodo, limite: 100 }
      });
      setAnalisisMasivo(res.data);
      toast.success(`Análisis completado: ${res.data.total_cuentas_analizadas} cuentas WAYA analizadas`);
    } catch (error) {
      toast.error('Error al ejecutar análisis masivo');
    } finally {
      setLoading(false);
    }
  };

  const generarAlertasVinculos = async () => {
    setGenerandoAlertas(true);
    try {
      const res = await api.post('/vinculos/analisis/generar-alertas', null, {
        params: { periodo, umbral_riesgo: 'ALTO' }
      });
      toast.success(`${res.data.alertas_generadas} alertas generadas`);
    } catch (error) {
      toast.error('Error al generar alertas');
    } finally {
      setGenerandoAlertas(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatMonto = (monto) => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(monto || 0);
  };

  const getNivelRiesgoColor = (nivel) => {
    switch (nivel) {
      case 'ALTO': return 'bg-red-100 text-red-700';
      case 'MEDIO': return 'bg-yellow-100 text-yellow-700';
      case 'BAJO': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPatronIcon = (tipo) => {
    switch (tipo) {
      case 'CONSOLIDACION': return <ArrowDownToLine size={16} className="text-red-600" />;
      case 'DISPERSION': return <ArrowUpFromLine size={16} className="text-orange-600" />;
      case 'CONEXION_COMPARTIDA_SALIDA':
      case 'CONEXION_COMPARTIDA_ENTRADA': return <Share2 size={16} className="text-purple-600" />;
      case 'MISMO_TITULAR': return <Users size={16} className="text-blue-600" />;
      default: return <Network size={16} className="text-slate-600" />;
    }
  };

  const filtrarPatrones = (patrones) => {
    if (tipoPatronFiltro === 'todos') return patrones;
    return patrones.filter(p => p.tipo_patron === tipoPatronFiltro || 
      (tipoPatronFiltro === 'CONEXION_COMPARTIDA' && p.tipo_patron?.startsWith('CONEXION_COMPARTIDA')));
  };

  const getAllPatrones = () => {
    if (!analisisCompleto) return [];
    const { consolidacion, dispersion, conexiones_compartidas, mismo_titular } = analisisCompleto.patrones;
    return [
      ...consolidacion,
      ...dispersion,
      ...conexiones_compartidas,
      ...mismo_titular
    ].sort((a, b) => (b.monto_total || 0) - (a.monto_total || 0));
  };

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
            Análisis de Vínculos
          </h1>
          <p className="text-slate-600 mt-1">
            Detecta patrones de conexión entre cuentas: consolidación, dispersión y conexiones compartidas
          </p>
        </div>
        <div className="flex gap-2 mt-4 lg:mt-0">
          <Button
            variant="outline"
            onClick={() => setIsConfigOpen(true)}
          >
            <Settings size={16} className="mr-2" />
            Configurar
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/m/monitoreo-transaccional/network-analysis')}
          >
            <Network size={16} className="mr-2" />
            Ver Grafo
          </Button>
        </div>
      </div>

      {/* Filtros Principales */}
      <Card className="p-4 mb-6 bg-white border-slate-200">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-sm text-slate-600">Período de Análisis</Label>
            <Select value={periodo} onValueChange={setPeriodo}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODOS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex-1 min-w-[150px]">
            <Label className="text-sm text-slate-600">Monto Mínimo</Label>
            <Input
              type="number"
              value={montoMinimo}
              onChange={(e) => setMontoMinimo(parseFloat(e.target.value) || 0)}
              placeholder="0"
            />
          </div>
          
          <Button
            onClick={ejecutarAnalisis}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
            Analizar
          </Button>
        </div>
      </Card>

      {/* Resumen */}
      {analisisCompleto && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="p-4 bg-white cursor-pointer hover:border-blue-300" onClick={() => setTipoPatronFiltro('todos')}>
            <div className="text-center">
              <p className="text-3xl font-bold text-slate-900">{analisisCompleto.resumen.total_patrones}</p>
              <p className="text-sm text-slate-500">Total Patrones</p>
            </div>
          </Card>
          
          <Card className="p-4 bg-red-50 border-red-200 cursor-pointer hover:border-red-400" onClick={() => setTipoPatronFiltro('CONSOLIDACION')}>
            <div className="flex items-center gap-2 justify-center">
              <ArrowDownToLine className="text-red-600" size={20} />
              <div className="text-center">
                <p className="text-2xl font-bold text-red-700">{analisisCompleto.resumen.consolidacion}</p>
                <p className="text-xs text-red-600">Consolidación</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-orange-50 border-orange-200 cursor-pointer hover:border-orange-400" onClick={() => setTipoPatronFiltro('DISPERSION')}>
            <div className="flex items-center gap-2 justify-center">
              <ArrowUpFromLine className="text-orange-600" size={20} />
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-700">{analisisCompleto.resumen.dispersion}</p>
                <p className="text-xs text-orange-600">Dispersión</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-purple-50 border-purple-200 cursor-pointer hover:border-purple-400" onClick={() => setTipoPatronFiltro('CONEXION_COMPARTIDA')}>
            <div className="flex items-center gap-2 justify-center">
              <Share2 className="text-purple-600" size={20} />
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-700">{analisisCompleto.resumen.conexiones_compartidas}</p>
                <p className="text-xs text-purple-600">Conexiones</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-blue-50 border-blue-200 cursor-pointer hover:border-blue-400" onClick={() => setTipoPatronFiltro('MISMO_TITULAR')}>
            <div className="flex items-center gap-2 justify-center">
              <Users className="text-blue-600" size={20} />
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-700">{analisisCompleto.resumen.mismo_titular}</p>
                <p className="text-xs text-blue-600">Mismo Titular</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white border border-slate-200 p-1 flex-wrap">
          <TabsTrigger value="patrones" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 text-slate-700">
            <AlertTriangle size={16} className="mr-2" />
            <span>Patrones Detectados</span>
          </TabsTrigger>
          <TabsTrigger value="cuenta" className="data-[state=active]:bg-green-50 data-[state=active]:text-green-700 text-slate-700">
            <Search size={16} className="mr-2" />
            <span>Buscar Cuenta</span>
          </TabsTrigger>
          <TabsTrigger value="cuit" className="data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700 text-slate-700">
            <Users size={16} className="mr-2" />
            <span>Por CUIT</span>
          </TabsTrigger>
          <TabsTrigger value="masivo" className="data-[state=active]:bg-red-50 data-[state=active]:text-red-700 text-slate-700">
            <TrendingUp size={16} className="mr-2" />
            <span>Análisis Masivo</span>
          </TabsTrigger>
          <TabsTrigger value="comparar" className="data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700 text-slate-700">
            <Share2 size={16} className="mr-2" />
            <span>Comparar</span>
          </TabsTrigger>
        </TabsList>

        {/* TAB: Patrones Detectados */}
        <TabsContent value="patrones">
          {analisisCompleto ? (
            <Card className="bg-white border-slate-200">
              {tipoPatronFiltro !== 'todos' && (
                <div className="p-3 bg-slate-50 border-b flex items-center justify-between">
                  <span className="text-sm text-slate-600">
                    Filtrando por: <strong>{tipoPatronFiltro.replace('_', ' ')}</strong>
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => setTipoPatronFiltro('todos')}>
                    Limpiar filtro
                  </Button>
                </div>
              )}
              
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Cuenta Principal</TableHead>
                    <TableHead>Conexiones</TableHead>
                    <TableHead>Transacciones</TableHead>
                    <TableHead>Monto Total</TableHead>
                    <TableHead>Riesgo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtrarPatrones(getAllPatrones()).map((patron, idx) => {
                    const key = `${patron.tipo_patron}-${idx}`;
                    const cuentaPrincipal = patron.cuenta_destino || patron.cuenta_origen || patron.cuenta_externa || patron.cuit;
                    const conexiones = patron.origenes_distintos || patron.destinos_distintos || patron.cantidad_cuentas_waya || patron.total_cuentas || 0;
                    
                    return (
                      <React.Fragment key={key}>
                        <TableRow 
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => toggleExpand(key)}
                        >
                          <TableCell>
                            {expandedRows[key] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getPatronIcon(patron.tipo_patron)}
                              <span className="text-sm font-medium">
                                {patron.tipo_patron?.replace(/_/g, ' ')}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="font-mono text-sm">
                              {cuentaPrincipal?.substring(0, 20)}...
                            </div>
                            {patron.es_cuenta_waya && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700">WAYA</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="font-bold">{conexiones}</span>
                            <span className="text-slate-500 text-sm ml-1">
                              {patron.tipo_patron === 'CONSOLIDACION' ? 'orígenes' : 
                               patron.tipo_patron === 'DISPERSION' ? 'destinos' : 
                               patron.tipo_patron?.includes('CONEXION') ? 'cuentas WAYA' : 'cuentas'}
                            </span>
                          </TableCell>
                          <TableCell>{patron.total_transacciones || patron.total_movimientos || 0}</TableCell>
                          <TableCell className="font-medium">{formatMonto(patron.monto_total)}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getNivelRiesgoColor(patron.nivel_riesgo)}`}>
                              {patron.nivel_riesgo}
                            </span>
                          </TableCell>
                        </TableRow>
                        
                        {expandedRows[key] && (
                          <TableRow>
                            <TableCell colSpan={7} className="bg-slate-50 p-4">
                              <div className="space-y-3">
                                {/* Descripción */}
                                {patron.descripcion && (
                                  <p className="text-sm text-slate-600">{patron.descripcion}</p>
                                )}
                                
                                {/* Fechas */}
                                {patron.primera_fecha && (
                                  <div className="text-sm">
                                    <span className="text-slate-500">Período: </span>
                                    {new Date(patron.primera_fecha).toLocaleDateString()} - {new Date(patron.ultima_fecha).toLocaleDateString()}
                                  </div>
                                )}
                                
                                {/* Lista de cuentas relacionadas */}
                                {(patron.lista_origenes || patron.lista_destinos || patron.cuentas_waya_involucradas) && (
                                  <div>
                                    <p className="text-sm font-medium text-slate-700 mb-2">
                                      {patron.lista_origenes ? 'Orígenes:' : patron.lista_destinos ? 'Destinos:' : 'Cuentas WAYA:'}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      {(patron.lista_origenes || patron.lista_destinos || patron.cuentas_waya_involucradas || []).slice(0, 10).map((cuenta) => (
                                        <Badge 
                                          key={cuenta} 
                                          variant="outline" 
                                          className={`font-mono text-xs ${cuenta?.startsWith('00003573') ? 'bg-green-50 text-green-700' : ''}`}
                                        >
                                          {cuenta?.substring(0, 15)}...
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Estadísticas adicionales */}
                                {(patron.origenes_waya !== undefined || patron.destinos_waya !== undefined) && (
                                  <div className="flex gap-4 text-sm">
                                    <div className="px-3 py-1 bg-green-100 rounded">
                                      <span className="text-green-700">WAYA: {patron.origenes_waya || patron.destinos_waya || 0}</span>
                                    </div>
                                    <div className="px-3 py-1 bg-slate-100 rounded">
                                      <span className="text-slate-700">Externos: {patron.origenes_externos || patron.destinos_externos || 0}</span>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Botones de acción */}
                                <div className="flex gap-2 mt-3">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCuentaBusqueda(cuentaPrincipal);
                                      buscarCuenta();
                                    }}
                                  >
                                    <Search size={14} className="mr-1" />
                                    Ver vínculos
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/m/monitoreo-transaccional/network-analysis?cuenta=${cuentaPrincipal}`);
                                    }}
                                  >
                                    <Network size={14} className="mr-1" />
                                    Ver en grafo
                                  </Button>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                  
                  {getAllPatrones().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                        <Network size={40} className="mx-auto mb-3 opacity-50" />
                        {analisisCompleto ? 'No se detectaron patrones con los filtros actuales' : 'Ejecute un análisis para ver los patrones detectados'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          ) : (
            <Card className="p-12 bg-white border-slate-200 text-center">
              <Network size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600 mb-4">Ejecute un análisis para detectar patrones de vínculos</p>
              <Button onClick={ejecutarAnalisis} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white">
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
                Ejecutar Análisis
              </Button>
            </Card>
          )}
        </TabsContent>

        {/* TAB: Buscar Cuenta */}
        <TabsContent value="cuenta">
          <Card className="p-6 bg-white border-slate-200 mb-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label>CBU/CVU de la Cuenta</Label>
                <Input
                  value={cuentaBusqueda}
                  onChange={(e) => setCuentaBusqueda(e.target.value)}
                  placeholder="Ej: 0000357301058..."
                  className="font-mono"
                  data-testid="cuenta-busqueda-input"
                />
              </div>
              <Button onClick={buscarCuenta} disabled={loading} data-testid="buscar-cuenta-btn">
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Search size={16} className="mr-2" />}
                Buscar
              </Button>
            </div>
          </Card>
          
          {loading && (
            <Card className="p-12 bg-white border-slate-200 text-center">
              <Loader2 className="animate-spin mx-auto text-blue-600 mb-4" size={40} />
              <p className="text-slate-600">Buscando vínculos de la cuenta...</p>
            </Card>
          )}
          
          {!loading && !cuentaVinculos && (
            <Card className="p-12 bg-white border-slate-200 text-center">
              <Search size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600">Ingrese un CBU/CVU y presione Buscar para ver los vínculos de la cuenta</p>
            </Card>
          )}
          
          {!loading && cuentaVinculos && (
            <div className="space-y-4">
              {/* Info de la cuenta */}
              <Card className="p-4 bg-white border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-lg">{cuentaVinculos.cuenta}</p>
                    {cuentaVinculos.es_waya && (
                      <Badge className="bg-green-100 text-green-700">Cuenta WAYA</Badge>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Período: {cuentaVinculos.periodo}</p>
                  </div>
                </div>
              </Card>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Salidas */}
                <Card className="p-4 bg-white border-slate-200">
                  <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <ArrowUpFromLine className="text-orange-600" size={18} />
                    Destinos ({cuentaVinculos.total_destinos})
                  </h3>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {cuentaVinculos.salidas.map((s, idx) => (
                      <div key={idx} className="p-2 bg-slate-50 rounded flex justify-between items-center">
                        <div>
                          <p className="font-mono text-sm">{s.destino?.substring(0, 20)}...</p>
                          {s.es_waya && <Badge variant="outline" className="text-xs bg-green-50">WAYA</Badge>}
                        </div>
                        <div className="text-right text-sm">
                          <p className="font-medium">{formatMonto(s.monto_total)}</p>
                          <p className="text-slate-500">{s.transacciones} txs</p>
                        </div>
                      </div>
                    ))}
                    {cuentaVinculos.salidas.length === 0 && (
                      <p className="text-slate-400 text-center py-4">Sin salidas en este período</p>
                    )}
                  </div>
                </Card>
                
                {/* Entradas */}
                <Card className="p-4 bg-white border-slate-200">
                  <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <ArrowDownToLine className="text-red-600" size={18} />
                    Orígenes ({cuentaVinculos.total_origenes})
                  </h3>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {cuentaVinculos.entradas.map((e, idx) => (
                      <div key={idx} className="p-2 bg-slate-50 rounded flex justify-between items-center">
                        <div>
                          <p className="font-mono text-sm">{e.origen?.substring(0, 20)}...</p>
                          {e.es_waya && <Badge variant="outline" className="text-xs bg-green-50">WAYA</Badge>}
                        </div>
                        <div className="text-right text-sm">
                          <p className="font-medium">{formatMonto(e.monto_total)}</p>
                          <p className="text-slate-500">{e.transacciones} txs</p>
                        </div>
                      </div>
                    ))}
                    {cuentaVinculos.entradas.length === 0 && (
                      <p className="text-slate-400 text-center py-4">Sin entradas en este período</p>
                    )}
                  </div>
                </Card>
              </div>
              
              {/* Cuentas WAYA relacionadas */}
              {cuentaVinculos.cuentas_waya_relacionadas?.length > 0 && (
                <Card className="p-4 bg-purple-50 border-purple-200">
                  <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                    <Share2 className="text-purple-600" size={18} />
                    Cuentas WAYA con destinos en común ({cuentaVinculos.cuentas_waya_relacionadas.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {cuentaVinculos.cuentas_waya_relacionadas.map((c, idx) => (
                      <Badge 
                        key={idx} 
                        variant="outline" 
                        className="font-mono cursor-pointer hover:bg-purple-100"
                        onClick={() => {
                          setCuenta1Comparar(cuentaVinculos.cuenta);
                          setCuenta2Comparar(c);
                          compararCuentas();
                        }}
                      >
                        {c?.substring(0, 15)}...
                      </Badge>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* TAB: Comparar Cuentas */}
        <TabsContent value="comparar">
          <Card className="p-6 bg-white border-slate-200 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <Label>Cuenta 1</Label>
                <Input
                  value={cuenta1Comparar}
                  onChange={(e) => setCuenta1Comparar(e.target.value)}
                  placeholder="CBU/CVU"
                  className="font-mono"
                />
              </div>
              <div>
                <Label>Cuenta 2</Label>
                <Input
                  value={cuenta2Comparar}
                  onChange={(e) => setCuenta2Comparar(e.target.value)}
                  placeholder="CBU/CVU"
                  className="font-mono"
                />
              </div>
              <Button onClick={compararCuentas} disabled={loading}>
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Share2 size={16} className="mr-2" />}
                Comparar
              </Button>
            </div>
          </Card>
          
          {comparacionResult && (
            <div className="space-y-4">
              {/* Conexión directa */}
              {comparacionResult.hay_conexion_directa && (
                <Card className="p-4 bg-red-50 border-red-200">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertTriangle size={20} />
                    <span className="font-bold">¡Conexión Directa Detectada!</span>
                    <span className="text-sm">Estas cuentas se transfieren entre sí</span>
                  </div>
                </Card>
              )}
              
              {/* Resumen */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 bg-white text-center">
                  <p className="text-2xl font-bold text-purple-700">{comparacionResult.total_destinos_comunes}</p>
                  <p className="text-sm text-slate-500">Destinos en común</p>
                </Card>
                <Card className="p-4 bg-white text-center">
                  <p className="text-2xl font-bold text-blue-700">{comparacionResult.total_origenes_comunes}</p>
                  <p className="text-sm text-slate-500">Orígenes en común</p>
                </Card>
                <Card className="p-4 bg-white text-center">
                  <p className="text-lg font-bold text-slate-700">{comparacionResult.resumen.cuenta1_destinos}/{comparacionResult.resumen.cuenta1_origenes}</p>
                  <p className="text-xs text-slate-500">Cuenta 1 (dest/orig)</p>
                </Card>
                <Card className="p-4 bg-white text-center">
                  <p className="text-lg font-bold text-slate-700">{comparacionResult.resumen.cuenta2_destinos}/{comparacionResult.resumen.cuenta2_origenes}</p>
                  <p className="text-xs text-slate-500">Cuenta 2 (dest/orig)</p>
                </Card>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Destinos comunes */}
                <Card className="p-4 bg-white border-slate-200">
                  <h3 className="font-bold text-slate-900 mb-3">Destinos en Común</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {comparacionResult.destinos_comunes.map((d, idx) => (
                      <Badge key={idx} variant="outline" className="font-mono block">
                        {d}
                      </Badge>
                    ))}
                    {comparacionResult.destinos_comunes.length === 0 && (
                      <p className="text-slate-400 text-center py-2">Sin destinos en común</p>
                    )}
                  </div>
                </Card>
                
                {/* Orígenes comunes */}
                <Card className="p-4 bg-white border-slate-200">
                  <h3 className="font-bold text-slate-900 mb-3">Orígenes en Común</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {comparacionResult.origenes_comunes.map((o, idx) => (
                      <Badge key={idx} variant="outline" className="font-mono block">
                        {o}
                      </Badge>
                    ))}
                    {comparacionResult.origenes_comunes.length === 0 && (
                      <p className="text-slate-400 text-center py-2">Sin orígenes en común</p>
                    )}
                  </div>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* TAB: Por CUIT */}
        <TabsContent value="cuit">
          <Card className="p-6 bg-white border-slate-200 mb-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label>CUIT/CUIL del Titular</Label>
                <Input
                  value={cuitBusqueda}
                  onChange={(e) => setCuitBusqueda(e.target.value)}
                  placeholder="Ej: 20-12345678-9 o 20123456789"
                  className="font-mono"
                  data-testid="cuit-busqueda-input"
                />
              </div>
              <Button onClick={buscarPorCuit} disabled={loading} data-testid="buscar-cuit-btn">
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Users size={16} className="mr-2" />}
                Buscar Cuentas del CUIT
              </Button>
            </div>
          </Card>
          
          {loading && (
            <Card className="p-12 bg-white border-slate-200 text-center">
              <Loader2 className="animate-spin mx-auto text-orange-600 mb-4" size={40} />
              <p className="text-slate-600">Analizando cuentas del CUIT...</p>
            </Card>
          )}
          
          {!loading && !cuitAnalisis && (
            <Card className="p-12 bg-white border-slate-200 text-center">
              <Users size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600">Ingrese un CUIT para ver todas las cuentas asociadas y su análisis de vínculos</p>
            </Card>
          )}
          
          {!loading && cuitAnalisis && (
            <div className="space-y-4">
              <Card className="p-4 bg-orange-50 border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-orange-900">CUIT: {cuitAnalisis.cuit}</h3>
                    <p className="text-orange-700">{cuitAnalisis.total_cuentas} cuentas encontradas en {cuitAnalisis.periodo}</p>
                  </div>
                </div>
              </Card>
              
              {cuitAnalisis.analisis_vinculos?.length > 0 ? (
                <Card className="bg-white border-slate-200">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Cuenta</TableHead>
                        <TableHead>WAYA</TableHead>
                        <TableHead>Orígenes</TableHead>
                        <TableHead>Destinos</TableHead>
                        <TableHead>Relacionadas</TableHead>
                        <TableHead>Riesgo</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cuitAnalisis.analisis_vinculos.map((cuenta, idx) => (
                        <TableRow key={idx} className="hover:bg-slate-50">
                          <TableCell className="font-mono text-sm">{cuenta.cuenta?.substring(0, 22)}...</TableCell>
                          <TableCell>
                            {cuenta.es_waya && <Badge className="bg-green-100 text-green-700">WAYA</Badge>}
                          </TableCell>
                          <TableCell className="text-center">{cuenta.total_origenes}</TableCell>
                          <TableCell className="text-center">{cuenta.total_destinos}</TableCell>
                          <TableCell className="text-center">{cuenta.cuentas_waya_relacionadas || 0}</TableCell>
                          <TableCell>
                            <Badge className={getNivelRiesgoColor(cuenta.nivel_riesgo)}>
                              {cuenta.nivel_riesgo}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => {
                                setCuentaBusqueda(cuenta.cuenta);
                                buscarCuenta();
                                setActiveTab('cuenta');
                              }}
                            >
                              <ExternalLink size={14} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              ) : (
                <Card className="p-8 bg-white text-center">
                  <p className="text-slate-500">No se encontraron cuentas con actividad para este CUIT</p>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* TAB: Análisis Masivo */}
        <TabsContent value="masivo">
          <Card className="p-6 bg-white border-slate-200 mb-4">
            <div className="flex flex-wrap gap-4 items-end justify-between">
              <div>
                <h3 className="font-bold text-slate-900">Análisis Masivo de Cuentas WAYA</h3>
                <p className="text-sm text-slate-600">Analiza todas las cuentas WAYA activas y detecta patrones de riesgo automáticamente</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={ejecutarAnalisisMasivo} 
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="analisis-masivo-btn"
                >
                  {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <TrendingUp size={16} className="mr-2" />}
                  Ejecutar Análisis Masivo
                </Button>
                <Button 
                  onClick={generarAlertasVinculos} 
                  disabled={generandoAlertas || !analisisMasivo}
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  {generandoAlertas ? <Loader2 className="animate-spin mr-2" size={16} /> : <AlertTriangle size={16} className="mr-2" />}
                  Generar Alertas
                </Button>
              </div>
            </div>
          </Card>
          
          {loading && (
            <Card className="p-12 bg-white border-slate-200 text-center">
              <Loader2 className="animate-spin mx-auto text-red-600 mb-4" size={40} />
              <p className="text-slate-600">Analizando cuentas WAYA... Esto puede tomar unos segundos</p>
            </Card>
          )}
          
          {!loading && !analisisMasivo && (
            <Card className="p-12 bg-white border-slate-200 text-center">
              <TrendingUp size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600 mb-2">Ejecuta el análisis masivo para evaluar todas las cuentas WAYA activas</p>
              <p className="text-xs text-slate-400">Se analizarán hasta 100 cuentas con mayor actividad</p>
            </Card>
          )}
          
          {!loading && analisisMasivo && (
            <div className="space-y-4">
              {/* Resumen de alertas */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 bg-white text-center">
                  <p className="text-3xl font-bold text-slate-900">{analisisMasivo.total_cuentas_analizadas}</p>
                  <p className="text-sm text-slate-500">Cuentas Analizadas</p>
                </Card>
                <Card className="p-4 bg-red-50 border-red-200 text-center">
                  <p className="text-3xl font-bold text-red-700">{analisisMasivo.alertas_por_nivel?.alto || 0}</p>
                  <p className="text-sm text-red-600">Riesgo Alto</p>
                </Card>
                <Card className="p-4 bg-yellow-50 border-yellow-200 text-center">
                  <p className="text-3xl font-bold text-yellow-700">{analisisMasivo.alertas_por_nivel?.medio || 0}</p>
                  <p className="text-sm text-yellow-600">Riesgo Medio</p>
                </Card>
                <Card className="p-4 bg-green-50 border-green-200 text-center">
                  <p className="text-3xl font-bold text-green-700">{analisisMasivo.alertas_por_nivel?.bajo || 0}</p>
                  <p className="text-sm text-green-600">Riesgo Bajo</p>
                </Card>
              </div>
              
              {/* Cuentas de alto riesgo */}
              {analisisMasivo.cuentas_alto_riesgo?.length > 0 && (
                <Card className="bg-white border-slate-200">
                  <div className="p-4 bg-red-50 border-b border-red-200">
                    <h3 className="font-bold text-red-900 flex items-center gap-2">
                      <AlertTriangle size={18} />
                      Cuentas de Alto Riesgo ({analisisMasivo.cuentas_alto_riesgo.length})
                    </h3>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Cuenta WAYA</TableHead>
                        <TableHead>Operaciones</TableHead>
                        <TableHead>Monto Total</TableHead>
                        <TableHead>Orígenes</TableHead>
                        <TableHead>Destinos</TableHead>
                        <TableHead>Razones de Riesgo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analisisMasivo.cuentas_alto_riesgo.map((cuenta, idx) => (
                        <TableRow key={idx} className="hover:bg-red-50">
                          <TableCell className="font-mono text-sm">{cuenta.cuenta?.substring(0, 22)}...</TableCell>
                          <TableCell className="text-center font-bold">{cuenta.operaciones}</TableCell>
                          <TableCell className="font-bold">{formatMonto(cuenta.monto_total)}</TableCell>
                          <TableCell className="text-center">{cuenta.origenes_distintos}</TableCell>
                          <TableCell className="text-center">{cuenta.destinos_distintos}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {cuenta.razones_riesgo?.map((razon, ridx) => (
                                <Badge key={ridx} variant="outline" className="text-xs bg-red-100 text-red-700">
                                  {razon}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
              
              {/* Cuentas de riesgo medio */}
              {analisisMasivo.cuentas_medio_riesgo?.length > 0 && (
                <Card className="bg-white border-slate-200">
                  <div className="p-4 bg-yellow-50 border-b border-yellow-200">
                    <h3 className="font-bold text-yellow-900 flex items-center gap-2">
                      <AlertTriangle size={18} />
                      Cuentas de Riesgo Medio ({analisisMasivo.cuentas_medio_riesgo.length})
                    </h3>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Cuenta WAYA</TableHead>
                        <TableHead>Operaciones</TableHead>
                        <TableHead>Monto Total</TableHead>
                        <TableHead>Orígenes</TableHead>
                        <TableHead>Destinos</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analisisMasivo.cuentas_medio_riesgo.slice(0, 10).map((cuenta, idx) => (
                        <TableRow key={idx} className="hover:bg-yellow-50">
                          <TableCell className="font-mono text-sm">{cuenta.cuenta?.substring(0, 22)}...</TableCell>
                          <TableCell className="text-center">{cuenta.operaciones}</TableCell>
                          <TableCell>{formatMonto(cuenta.monto_total)}</TableCell>
                          <TableCell className="text-center">{cuenta.origenes_distintos}</TableCell>
                          <TableCell className="text-center">{cuenta.destinos_distintos}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Config Dialog */}
      <Dialog open={isConfigOpen} onOpenChange={setIsConfigOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Configuración de Umbrales</DialogTitle>
            <DialogDescription>
              Ajusta los umbrales para la detección de patrones
            </DialogDescription>
          </DialogHeader>
          
          {config && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Umbral de Consolidación (orígenes distintos)</Label>
                <Input
                  type="number"
                  value={config.consolidacion_umbral}
                  onChange={(e) => updateConfig('consolidacion_umbral', parseInt(e.target.value))}
                />
                <p className="text-xs text-slate-500 mt-1">Mínimo de orígenes para detectar consolidación</p>
              </div>
              
              <div>
                <Label>Umbral de Dispersión (destinos distintos)</Label>
                <Input
                  type="number"
                  value={config.dispersion_umbral}
                  onChange={(e) => updateConfig('dispersion_umbral', parseInt(e.target.value))}
                />
                <p className="text-xs text-slate-500 mt-1">Mínimo de destinos para detectar dispersión</p>
              </div>
              
              <div>
                <Label>Umbral de Conexiones Compartidas</Label>
                <Input
                  type="number"
                  value={config.conexiones_compartidas_umbral}
                  onChange={(e) => updateConfig('conexiones_compartidas_umbral', parseInt(e.target.value))}
                />
                <p className="text-xs text-slate-500 mt-1">Mínimo de cuentas WAYA con mismo destino/origen</p>
              </div>
              
              <div>
                <Label>Prefijo WAYA</Label>
                <Input
                  value={config.waya_prefix}
                  onChange={(e) => updateConfig('waya_prefix', e.target.value)}
                  className="font-mono"
                />
                <p className="text-xs text-slate-500 mt-1">Prefijo para identificar cuentas WAYA</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfigOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VinculosAnalysis;
